/**
 * Windowed rendering for the Problems list (W2-12a checker-runtime; DESIGN.md "virtualised lists (Lines, History,
 * Problems)"). Problem rows differ in height (the WHY line wraps, fixes add buttons), so the window works on
 * MEASURED heights: every rendered row is observed (ResizeObserver) and rows never measured count as
 * `ROW_ESTIMATE`. Only the rows that intersect the scroll viewport (plus `OVERSCAN_PX` above and below) are in the
 * DOM, together with the rows the caller forces (the current problem and the row that holds the keyboard focus), so
 * revealing a problem deep in a list of thousands renders a few dozen rows, not every row up to it.
 *
 * The rows are absolutely positioned at their offsets inside the scroll container (one element as tall as all
 * rows together). When a row above the viewport changes height, the scroll position moves by the same amount so
 * the visible rows stay put. Without layout (tests, first paint) the viewport counts as `FALLBACK_VIEWPORT_PX`.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/** Height assumed for a row that has not been measured yet (px). */
export const ROW_ESTIMATE = 64;
/** Rows rendered beyond the viewport, above and below (px). */
export const OVERSCAN_PX = 480;
/** Viewport height assumed before the scroll container has been measured (px). */
export const FALLBACK_VIEWPORT_PX = 800;

/** Top offset of every row plus the total height at the end (length = keys + 1). */
export function rowOffsets(
  keys: readonly string[],
  heights: ReadonlyMap<string, number>,
  estimate = ROW_ESTIMATE,
): Float64Array {
  const out = new Float64Array(keys.length + 1);
  let y = 0;
  for (let i = 0; i < keys.length; i++) {
    out[i] = y;
    y += heights.get(keys[i] as string) ?? estimate;
  }
  out[keys.length] = y;
  return out;
}

/** Rows [first, last) that intersect the band [from, to) of the list (offsets from `rowOffsets`). */
export function rowRange(offsets: Float64Array, from: number, to: number): readonly [first: number, last: number] {
  const n = offsets.length - 1;
  if (n <= 0 || to <= from) return [0, 0];
  // First row whose bottom is below `from`.
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if ((offsets[mid + 1] as number) <= from) lo = mid + 1;
    else hi = mid;
  }
  const first = lo;
  // First row whose top is at or below `to`.
  hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if ((offsets[mid] as number) < to) lo = mid + 1;
    else hi = mid;
  }
  return [first, Math.max(first, lo)];
}

/** Sorted unique indices of the window plus the forced rows (out-of-range ones dropped). */
export function windowIndices(range: readonly [number, number], forced: readonly number[], count: number): number[] {
  const set = new Set<number>();
  for (let i = range[0]; i < range[1]; i++) set.add(i);
  for (const i of forced) if (i >= 0 && i < count) set.add(i);
  return [...set].sort((a, b) => a - b);
}

export interface VirtualRows {
  /** Offsets of the rows (see `rowOffsets`). */
  readonly offsets: Float64Array;
  /** Height of all rows together (px). */
  readonly total: number;
  /** Rows to render, in order. */
  readonly indices: readonly number[];
  /** Ref callback for a row element; the element must carry `data-vrow` = its key. */
  readonly measure: (el: HTMLElement | null) => (() => void) | undefined;
  /** Rows that fit in the viewport (PageUp / PageDown step). */
  readonly pageRows: number;
}

/** Attribute that names the key of a row element (read by the size observer). */
export const ROW_KEY_ATTR = 'data-vrow';

interface ScrollFacts {
  readonly top: number;
  readonly viewport: number;
}

interface Live {
  readonly keys: readonly string[];
  readonly heights: ReadonlyMap<string, number>;
}

function borderBoxHeight(entry: ResizeObserverEntry): number {
  const box = entry.borderBoxSize[0];
  if (box !== undefined && box.blockSize > 0) return box.blockSize;
  return entry.target.getBoundingClientRect().height;
}

export function useVirtualRows(opts: {
  readonly keys: readonly string[];
  /** The scroll container; it holds the rows directly (positioned from its top). */
  readonly scrollRef: RefObject<HTMLElement | null>;
  /** Rows that are always rendered (current problem, focused row); -1 entries are ignored. */
  readonly forced: readonly number[];
}): VirtualRows {
  const { keys, scrollRef, forced } = opts;
  const [heights, setHeights] = useState<ReadonlyMap<string, number>>(() => new Map());
  const [facts, setFacts] = useState<ScrollFacts>({ top: 0, viewport: 0 });
  const live = useRef<Live>({ keys, heights });
  const observer = useRef<ResizeObserver | null>(null);

  useLayoutEffect(() => {
    live.current = { keys, heights };
  });

  // Scroll position and viewport height of the scroll container (the observer also reports its first size).
  useEffect(() => {
    const scroller = scrollRef.current;
    if (scroller === null) return;
    const read = (): void => {
      const next: ScrollFacts = { top: scroller.scrollTop, viewport: scroller.clientHeight };
      setFacts((f) => (f.top === next.top && f.viewport === next.viewport ? f : next));
    };
    scroller.addEventListener('scroll', read, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(read);
    ro?.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', read);
      ro?.disconnect();
    };
  }, [scrollRef]);

  const onRowsResized = useCallback(
    (entries: readonly ResizeObserverEntry[]): void => {
      const { keys: ks, heights: prev } = live.current;
      const scroller = scrollRef.current;
      const offsets = rowOffsets(ks, prev);
      const visibleTop = scroller?.scrollTop ?? 0;
      const next = new Map(prev);
      let changed = false;
      let shift = 0;
      for (const entry of entries) {
        const key = entry.target.getAttribute(ROW_KEY_ATTR);
        if (key === null) continue;
        const h = borderBoxHeight(entry);
        if (!(h > 0)) continue;
        const old = prev.get(key) ?? ROW_ESTIMATE;
        if (Math.abs(h - old) < 0.5) continue;
        next.set(key, h);
        changed = true;
        const i = ks.indexOf(key);
        // A row entirely above the viewport grew or shrank: keep the visible rows where they are.
        if (i >= 0 && (offsets[i] ?? 0) + old <= visibleTop) shift += h - old;
      }
      if (!changed) return;
      // Forget rows that left the list once they are most of the map.
      if (next.size > 2 * ks.length + 64) {
        const present = new Set(ks);
        for (const key of [...next.keys()]) if (!present.has(key)) next.delete(key);
      }
      live.current = { keys: ks, heights: next };
      setHeights(next);
      if (shift !== 0 && scroller !== null) scroller.scrollTop += shift;
    },
    [scrollRef],
  );

  const measure = useCallback(
    (el: HTMLElement | null): (() => void) | undefined => {
      if (el === null || typeof ResizeObserver === 'undefined') return undefined;
      observer.current ??= new ResizeObserver(onRowsResized);
      const ro = observer.current;
      ro.observe(el);
      return () => {
        ro.unobserve(el);
      };
    },
    [onRowsResized],
  );

  useEffect(
    () => () => {
      observer.current?.disconnect();
      observer.current = null;
    },
    [],
  );

  const offsets = rowOffsets(keys, heights);
  const viewport = facts.viewport > 0 ? facts.viewport : FALLBACK_VIEWPORT_PX;
  const indices = windowIndices(
    rowRange(offsets, facts.top - OVERSCAN_PX, facts.top + viewport + OVERSCAN_PX),
    forced,
    keys.length,
  );
  return {
    offsets,
    total: offsets[keys.length] ?? 0,
    indices,
    measure,
    pageRows: Math.max(1, Math.floor(viewport / ROW_ESTIMATE) - 1),
  };
}
